// Walking a session, on the client.
//
// The server says what the circuits ARE (routes/execution.go); this decides what
// the athlete is looking at right now — which set, which station, and what
// happens when they finish a set.
//
// # WHY A CURSOR AND NOT A LIST
//
// A superset is performed A1 → rest 10s → A2 → rest 120s → A1 → … and an athlete
// mid-circuit needs to know exactly one thing: what is in front of me. A screen
// showing all stations with all their sets is a reference document; this is a
// position in a sequence.

import { REST_ROLE } from '../phaseGrid';

/**
 * Where the athlete is: a group, a SET within it, and a station within that.
 *
 * `set` counts passes through the group. For the station being performed that
 * is exactly its own set number — A1 on the third pass is on A1's third set —
 * which is why the screen can say "Set 3 of 5" without any translation.
 */
export function startAt(groups) {
  const list = groups ?? [];
  if (list.length === 0) return null;
  return { group: 0, set: 1, station: 0 };
}

/** The group a cursor points at. */
export function currentGroup(groups, cursor) {
  if (!cursor) return null;
  return (groups ?? [])[cursor.group] ?? null;
}

/** The station a cursor points at. */
export function currentStation(groups, cursor) {
  const group = currentGroup(groups, cursor);
  if (!group) return null;
  return group.stations?.[cursor.station] ?? null;
}

/**
 * The target for the station under the cursor, on this set.
 *
 * Empty when the station has run out — a shorter partner in a longer circuit.
 */
export function currentTarget(groups, cursor) {
  const station = currentStation(groups, cursor);
  if (!station) return '';
  return station.targets?.[cursor.set - 1] ?? '';
}

/**
 * Whether the station under the cursor still has work left.
 *
 * A2 with 3 sets beside an A1 with 5 is finished after its third, and
 * must be skipped rather than shown with an empty target.
 */
export function stationIsActive(groups, cursor) {
  const station = currentStation(groups, cursor);
  if (!station) return false;
  return cursor.set <= (station.targets?.length ?? 0);
}

/**
 * Advance past the station just completed.
 *
 * Walks the circuit: next station on this pass, else the next set, else the next
 * group. Returns null when the session is done.
 *
 * Stations that have run out are skipped, so a shorter partner drops out of the
 * later sets instead of presenting a set with nothing prescribed.
 */
export function advance(groups, cursor) {
  if (!cursor) return null;
  const list = groups ?? [];

  let { group, set, station } = cursor;
  // Bounded by the total number of positions, so a malformed group — every
  // station empty — terminates instead of looping.
  const limit = totalSteps(list) + 1;

  for (let step = 0; step < limit; step += 1) {
    const current = list[group];
    if (!current) return null;

    station += 1;
    if (station >= (current.stations?.length ?? 0)) {
      station = 0;
      set += 1;
    }
    if (set > (current.rounds ?? 0)) {
      group += 1;
      set = 1;
      station = 0;
      if (group >= list.length) return null;
    }

    const next = { group, set, station };
    if (stationIsActive(list, next)) return next;
  }
  return null;
}

/** Every position in the session, for a progress count. */
export function totalSteps(groups) {
  let total = 0;
  for (const group of groups ?? []) {
    for (const station of group.stations ?? []) {
      total += station.targets?.length ?? 0;
    }
  }
  return total;
}

/**
 * How many positions have been completed.
 *
 * # WHY A NULL CURSOR IS EVERY POSITION, NOT NONE
 *
 * `advance` returns null when the last set is done, so a null cursor means the
 * athlete is PAST the end — every position is behind them. Returning 0 there
 * made the completion screen tell someone who had just logged all eight sets
 * "0 of 8 sets logged", because ExecutionLogger renders that count inside its
 * own `!cursor` branch: the one screen that displays the number is the one
 * screen where the cursor is always null, so `done` was structurally 0.
 *
 * The empty case stays honest by arithmetic rather than by a special case.
 * `startAt` returns null only for an empty group list, and `totalSteps` of an
 * empty list is 0, so a session with nothing in it still reports "0 of 0"
 * rather than claiming work that was never prescribed.
 */
export function stepsDone(groups, cursor) {
  if (!cursor) return totalSteps(groups);
  let done = 0;

  const list = groups ?? [];
  for (let g = 0; g < list.length; g += 1) {
    const group = list[g];
    for (let s = 0; s < (group.stations?.length ?? 0); s += 1) {
      const targets = group.stations[s].targets?.length ?? 0;
      for (let r = 1; r <= targets; r += 1) {
        const before = g < cursor.group
          || (g === cursor.group && r < cursor.set)
          || (g === cursor.group && r === cursor.set && s < cursor.station);
        if (before) done += 1;
      }
    }
  }
  return done;
}

/**
 * How many sets are done, as the progress bar should show it.
 *
 * # THE BUG THIS FIXES
 *
 * During rest the cursor still points at the set just logged. It moves only
 * when the rest ends (goNext). stepsDone counts what is BEHIND the cursor, so for
 * the whole rest after set 1 the bar read 0/11, telling the athlete that nothing
 * had been logged at the exact moment they were looking at the screen. Every
 * rest, every set, one behind.
 *
 * While resting, the set in front of the cursor is done. It is counted as if
 * the cursor had already moved on.
 */
export function progressDone(groups, cursor, resting) {
  if (!resting) return stepsDone(groups, cursor);
  return stepsDone(groups, advance(groups, cursor));
}

/**
 * Which set of how many, for the station being performed.
 *
 * # WHY THE DENOMINATOR IS NOT group.rounds
 *
 * `rounds` is the LARGEST set count in the group, not a shared one — A1 with 5
 * sets beside A2 with 3 gives rounds = 5. Using it as the denominator tells
 * someone on A2's final set that they are on "3 of 5", promising two sets that
 * were never prescribed and that the cursor will never reach.
 *
 * The station's own targets are the authority — the same list stationIsActive
 * uses to decide when a station has run out — so the two can never disagree
 * about how much work is left.
 *
 * @returns {{index: number, total: number}}
 */
export function setPosition(groups, cursor) {
  const station = currentStation(groups, cursor);
  const total = station?.targets?.length ?? 0;
  return { index: cursor?.set ?? 1, total };
}

/**
 * What comes after the set being performed — the rest, and what it is for.
 *
 * This is the whole reason the circuit is modelled rather than listed. The same
 * 90 seconds is a walk to the next station, a recovery before the next set, or
 * a pause between straight sets, and an athlete told only "90" cannot tell which.
 *
 * @returns {{seconds: number, role: string, nextLabel: string}|null}
 */
export function restAfter(groups, cursor) {
  const station = currentStation(groups, cursor);
  if (!station) return null;

  const next = advance(groups, cursor);
  const seconds = Number(station.rest) || 0;

  if (!next) {
    return { seconds: 0, role: 'done', nextLabel: 'Session complete' };
  }

  const nextStation = currentStation(groups, next);
  const nextGroup = currentGroup(groups, next);
  const label = next.group !== cursor.group
    ? `${nextGroup?.series ?? ''} · ${nextStation?.exerciseName ?? ''}`
    : `${nextStation?.series ?? ''} · ${nextStation?.exerciseName ?? ''}`;

  return { seconds, role: station.restRole || 'between-sets', nextLabel: label };
}

/**
 * The rest that follows this set, as something to READ rather than count down.
 *
 * # WHY THE SET SCREEN NEEDS IT AND THE TIMER IS NOT ENOUGH
 *
 * Rest used to exist only after the set was logged, in RestTimer. So an athlete
 * standing under the bar could see how many reps and how fast, and nothing about
 * what came next. Ten seconds to a partner exercise and three minutes on the
 * floor are the difference between a superset and a strength single, and that
 * changes how hard you go on the set you are ABOUT to do — not after it.
 *
 * Returns display strings rather than a component's worth of logic, so the
 * wording is testable without rendering, and `spoken` gives a screen reader the
 * unabbreviated form instead of "120s".
 *
 * @returns {{value: string, label: string, spoken: string}}
 */
export function restPreview(groups, cursor) {
  return stationRestPreview(currentStation(groups, cursor));
}

/**
 * The same preview as `restPreview`, for a station directly — no cursor
 * needed.
 *
 * `restPreview` reads the station the cursor is ON; the pre-flight "get set
 * up" screen (series-preflight-gate.md) describes stations the cursor has not
 * reached yet — a superset's A2 while the athlete is still looking at A1's
 * card — so it needs this without pretending a cursor points there.
 *
 * @returns {{value: string, label: string, spoken: string}}
 */
export function stationRestPreview(station) {
  const seconds = Number(station?.rest) || 0;

  if (!station || seconds <= 0) {
    // No rest is a real prescription — a giant set runs straight through — and
    // saying so is better than an empty box where every other number lives.
    return { value: 'none', label: 'rest', spoken: 'none prescribed' };
  }

  const role = station.restRole || REST_ROLE.BETWEEN_SETS;
  return {
    value: seconds < 60 ? `${seconds}s` : clockish(seconds),
    label: REST_PURPOSE[role] || 'rest',
    spoken: `${spokenSeconds(seconds)}, ${REST_PURPOSE[role] || 'rest'}`,
  };
}

/**
 * What the rest is FOR, in the athlete's language.
 *
 * Deliberately not "rounds": a transition is a walk to the partner exercise, and
 * the long one is recovery before the next set of this exercise.
 */
const REST_PURPOSE = {
  [REST_ROLE.TRANSITION]: 'to next',
  [REST_ROLE.BETWEEN_GROUPS]: 'recovery',
  [REST_ROLE.BETWEEN_SETS]: 'between sets',
};

/** 90 reads as 1:30 to anyone standing under a bar. */
function clockish(seconds) {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest === 0 ? `${minutes}m` : `${minutes}:${String(rest).padStart(2, '0')}`;
}

function spokenSeconds(seconds) {
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  const head = `${minutes} minute${minutes === 1 ? '' : 's'}`;
  return rest === 0 ? head : `${head} ${rest} seconds`;
}

/**
 * Break a tempo into its four phases, for display.
 *
 * A tempo is a prescription, not decoration: "4-0-1-0" means a four-second
 * lowering, and an athlete who reads it as a label lifts it in one. Spelling out
 * the phases is the difference between showing the notation and prescribing it.
 *
 * @returns {Array<{seconds: string, label: string}>|null}
 */
export function tempoPhases(tempo) {
  const raw = String(tempo ?? '').trim();
  if (!raw) return null;

  const parts = raw.split('-');
  if (parts.length !== 4) return null;

  const labels = ['Lower', 'Pause', 'Lift', 'Pause'];
  return parts.map((value, i) => ({
    seconds: value.toUpperCase() === 'X' ? 'X' : value,
    label: labels[i],
  }));
}

/** The tempo phase an athlete most needs to notice: a long eccentric. */
export function tempoEmphasis(tempo) {
  const phases = tempoPhases(tempo);
  if (!phases) return null;
  const lower = Number(phases[0].seconds);
  return Number.isFinite(lower) && lower >= 3
    ? `${lower} seconds down`
    : null;
}

/**
 * The backend's equipment vocabulary (routes/execution.go's `equipment`
 * field, in turn models/equipment.go's `Equipment.String()`) is lowercase
 * and snake-ish — written to be parsed, not read. This is the one place
 * that turns it into something an athlete reads on the gate.
 *
 * Unrecognised tokens (none expected today; a future backend vocabulary
 * addition this frontend hasn't learned about yet) pass through unchanged
 * rather than rendering blank or throwing.
 */
const EQUIPMENT_LABELS = {
  dumbbell: 'Dumbbell',
  barbell: 'Barbell',
  bench: 'Bench',
  rack: 'Rack',
  pull_up_bar: 'Pull-Up Bar',
  cable: 'Cable',
  machine: 'Machine',
  swiss_ball: 'Swiss Ball',
  band: 'Band',
  sled: 'Sled',
};

export function equipmentLabel(token) {
  return EQUIPMENT_LABELS[token] ?? token;
}

/**
 * Every station in a series needs equipment once, combined — not once per
 * station. An athlete preparing for a superset grabs everything before
 * pressing Start, not station by station (series-equipment-checklist.md).
 *
 * First-seen order across stations, deduplicated, already run through
 * equipmentLabel — the gate renders this directly.
 *
 * @returns {string[]}
 */
export function groupEquipment(group) {
  const seen = new Set();
  const labels = [];
  for (const station of group?.stations ?? []) {
    for (const token of station?.equipment ?? []) {
      if (!seen.has(token)) {
        seen.add(token);
        labels.push(equipmentLabel(token));
      }
    }
  }
  return labels;
}
